import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_EVENT } from '../../gql/mutations';
import { GET_ALL_MEMBERS, GET_ALL_EVENTS, GET_ALL_USERS_EVENTS/*, GET_USERS_QB_OPENS*/ } from '../../gql/queries';
import Multiselect from 'multiselect-react-dropdown';
import { useMediaQuery } from '../../../../utils/deviceChecker';
import './CreateEventMultiStep.css';

const HostList = ({ members, onHostsChange }) => {
    const [hostRows, setHostRows] = useState(['']);
    const [searchTerms, setSearchTerms] = useState(['']);
    const [activeDropdown, setActiveDropdown] = useState(null);

    const addRow = () => {
        setHostRows([...hostRows, '']);
        setSearchTerms([...searchTerms, '']);
    };

    const removeRow = (index) => {
        if (hostRows.length > 1) {
            const newRows = hostRows.filter((_, i) => i !== index);
            const newSearchTerms = searchTerms.filter((_, i) => i !== index);
            setHostRows(newRows);
            setSearchTerms(newSearchTerms);
            onHostsChange(newRows.filter(id => id !== ''));
        }
    };

    const updateSearchTerm = (index, value) => {
        const newSearchTerms = [...searchTerms];
        newSearchTerms[index] = value;
        setSearchTerms(newSearchTerms);

        const newRows = [...hostRows];
        newRows[index] = '';
        setHostRows(newRows);
        onHostsChange(newRows.filter(id => id !== ''));

        setActiveDropdown(index);
    };

    const selectHost = (index, member) => {
        const newRows = [...hostRows];
        const newSearchTerms = [...searchTerms];
        newRows[index] = member._id;
        newSearchTerms[index] = member.fullName;
        setHostRows(newRows);
        setSearchTerms(newSearchTerms);
        setActiveDropdown(null);
        onHostsChange(newRows.filter(id => id !== ''));
    };

    const handleBlur = (index) => {
        setTimeout(() => {
            setActiveDropdown(null);
        }, 200);
    };

    const getFilteredMembers = (index) => {
        const searchTerm = searchTerms[index] || '';
        if (!searchTerm) return [];
        return members.filter(member =>
            member.fullName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    return (
        <div className="host-list">
            {hostRows.map((hostId, index) => (
                <div key={index} className="host-row">
                    <div className="host-input-wrapper">
                        <input
                            type="text"
                            value={searchTerms[index] || ''}
                            onChange={(e) => updateSearchTerm(index, e.target.value)}
                            onFocus={() => setActiveDropdown(index)}
                            onBlur={() => handleBlur(index)}
                            placeholder="Type to search..."
                            className="host-input"
                            autoComplete="off"
                        />
                        {activeDropdown === index && searchTerms[index] && (
                            <div className="host-dropdown">
                                {getFilteredMembers(index).length > 0 ? (
                                    getFilteredMembers(index).map(member => (
                                        <div
                                            key={member._id}
                                            className="host-option"
                                            onMouseDown={() => selectHost(index, member)}
                                        >
                                            {member.fullName}
                                        </div>
                                    ))
                                ) : (
                                    <div className="host-no-results">No members found</div>
                                )}
                            </div>
                        )}
                    </div>
                    {hostRows.length > 1 && (
                        <button
                            className="remove-host-button"
                            onClick={() => removeRow(index)}
                            title="Remove host"
                        >
                            −
                        </button>
                    )}
                </div>
            ))}

            <button className="add-host-button" onClick={addRow}>
                + Add Host
            </button>
        </div>
    );
};

const CreateEventMultiStep = ({ showModal }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        eventType: '',
        venue: '',
        date: '',
        time: '',
        hijriDate: '',
        hijriDay: '',
        hijriMonth: '',
        program: '',
        eventAudience: '',
        isUmoorEvent: false,
        umoorType: '',
        occasion: '',
        hosts: [],
        isAtMarkaz: false,
        address: '',
        eventDetails: {
            eventName: '',
            program: ''
        }
    });
    const [isHovering, setIsHovering] = useState(false);
    const [hostSearchTerm, setHostSearchTerm] = useState('');
    const [showHostDropdown, setShowHostDropdown] = useState(false);

    const { data: memberData } = useQuery(GET_ALL_MEMBERS);
    const members = memberData?.allMembers || [];

    // const { data: qbOpensData } = useQuery(GET_USERS_QB_OPENS);
    // const qbOpens = qbOpensData?.usersQBOpens || [];

    const categories = [
        "Darees", "Shitabi", "Tasbeeh", "Waras", "Shaadi", "Aqiqa",
        "Umoor Sehat/Sports", "Umoor Iqtesadiyah", "Umoor FMB",
        "Umoor Deeniyah", "Umoor Talimiyah", "Umoor Kharejiyah- PR"
    ];

    const [createEvent] = useMutation(CREATE_EVENT, {
        refetchQueries: [
            { query: GET_ALL_EVENTS },
            { query: GET_ALL_USERS_EVENTS }
        ]
    });
    const isDesktop = useMediaQuery("(min-width: 1258px)");

    const steps = [
        {
            field: 'intro',
            type: 'intro'
        },
        {
            title: "Who is invited?",
            field: 'eventType',
            type: 'radio',
            options: [
                { value: 'public', label: 'Everyone in the jamaat' },
                { value: 'private', label: 'I will be sending personal invitations' }
            ]
        },
        {
            title: "Who is it for?",
            field: 'eventAudience',
            type: 'radio',
            options: [
                { value: 'men', label: 'Mardo only' },
                { value: 'women', label: 'Bairao only' },
                { value: 'everyone', label: 'Mardo and Bairao both' }
            ]
        },
        {
            title: "Is this event organized by an Umoor?",
            field: 'isUmoorEvent',
            type: 'radio',
            options: [
                { value: true, label: 'Yes, this is a committee hosted event' },
                { value: false, label: 'No, this is a personal event' }
            ]
        },
        {
            title: "Select the Umoor",
            field: 'umoorType',
            type: 'select',
            options: [
                "Deeniyah",
                "Taalimiyah",
                "Maaliya",
                "Marafiq Burhaniyah",
                "Mawarid Bashariya",
                "Iqtesadiyah",
                "Kharejiyah",
                "Dakheliyah",
                "Sehat",
                "FMB",
                "Qaza",
                "Amlaak"
            ],
            condition: (formData) => formData.isUmoorEvent === true
        },
        {
            title: "Choose the occasion",
            field: 'occasion',
            type: 'radio',
            options: [
                { value: 'Darees', label: 'Darees' },
                { value: 'Tasbeeh', label: 'Tasbeeh' },
                { value: 'Shitabi', label: 'Shitabi' },
                { value: 'Maatam Majlis', label: 'Maatam Majlis' },
                { value: 'Waras', label: 'Waras' },
                { value: 'Misaaq', label: 'Misaaq' },
                { value: 'Nikaah', label: 'Nikaah' },
                { value: 'Shaadi', label: 'Shaadi' },
                { value: 'Chhatti', label: 'Chhatti' },
                { value: 'Sipara', label: 'Sipara' }
            ],
            condition: (formData) => formData.isUmoorEvent === false
        },
        {
            title: "When is your Event",
            field: 'dateTime',
            type: 'dateTime'
        },
        {
            title: "Add all hosts (including yourself)",
            field: 'hosts',
            type: 'hostList'
        },
        {
            title: "Provide more details for attendees",
            field: 'eventDetails',
            type: 'eventDetails'
        },
        {
            title: "Is your event at Markaz?",
            field: 'isAtMarkaz',
            type: 'radio',
            options: [
                { value: true, label: 'Yes, it is at Markaz' },
                { value: false, label: 'No, I will give the address' }
            ]
        },
        {
            title: "Event address",
            field: 'address',
            type: 'address',
            condition: (formData) => formData.isAtMarkaz === false
        },
        // {
        //     title: "Clearance",
        //     field: 'clearance',
        //     type: 'clearance'
        // },
        {
            title: "Confirm Your Event Details",
            field: 'confirmation',
            type: 'confirmation'
        },
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const nextStep = () => {
        if (currentStep < steps.length - 1) {
            const nextStepIndex = currentStep + 1;
            const nextStepData = steps[nextStepIndex];
            if (nextStepData.condition && !nextStepData.condition(formData)) {
                let skipTo = nextStepIndex + 1;
                while (skipTo < steps.length && steps[skipTo].condition && !steps[skipTo].condition(formData)) {
                    skipTo++;
                }
                if (skipTo < steps.length) {
                    setCurrentStep(skipTo);
                }
            } else {
                setCurrentStep(nextStepIndex);
            }
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            const prevStepIndex = currentStep - 1;
            const prevStepData = steps[prevStepIndex];
            if (prevStepData.condition && !prevStepData.condition(formData)) {
                let skipTo = prevStepIndex - 1;
                while (skipTo >= 0 && steps[skipTo].condition && !steps[skipTo].condition(formData)) {
                    skipTo--;
                }
                if (skipTo >= 0) {
                    setCurrentStep(skipTo);
                }
            } else {
                setCurrentStep(prevStepIndex);
            }
        }
    };

    const handleSubmit = async () => {
        try {
            const hijriDateString = formData.hijriDay && formData.hijriMonth
                ? `${formData.hijriDay} ${formData.hijriMonth}`
                : '';
            const category = formData.isUmoorEvent ? formData.umoorType : formData.occasion;
            const venue = formData.isAtMarkaz ? 'Markaz' : formData.address;

            await createEvent({
                variables: {
                    title: formData.eventDetails.eventName || formData.title,
                    eventType: formData.eventType,
                    venue: venue,
                    category: category,
                    date: formData.date,
                    hijriDate: hijriDateString,
                    time: formData.time,
                    program: formData.eventDetails.program || formData.program,
                    hosts: formData.hosts,
                    womenOnly: formData.eventAudience === 'women',
                    menOnly: formData.eventAudience === 'men'
                },
            });
            window.location.href = '/events';
        } catch (err) {
            console.error(err);
        }
    };

    const currentStepData = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;
    const canProceed = currentStepData.type === 'intro' ||
        currentStepData.type === 'clearance' ||
        currentStepData.type === 'confirmation' ||
        (currentStepData.type === 'eventDetails' ?
            formData.eventDetails.eventName?.trim() && formData.eventDetails.program?.trim() :
            currentStepData.type === 'dateTime' ?
                formData.date && formData.time :
                (formData[currentStepData.field] !== undefined && formData[currentStepData.field] !== '' &&
                    (currentStepData.field !== 'hosts' || formData.hosts.length > 0))) ||
        (currentStepData.condition && !currentStepData.condition(formData));

    const renderInput = () => {
        switch (currentStepData.type) {
            case 'hostList':
                return (
                    <HostList
                        members={members}
                        onHostsChange={(hosts) => handleInputChange('hosts', hosts)}
                    />
                );
            case 'intro':
                return (
                    <div className="intro-content">
                        <div className="intro-note">
                            <strong>Important:</strong>
                            <ol className="intro-steps">
                                <li>Please review the date in the calendar above and avoid clashes</li>
                                <li>Download a clearance letter from <a href="https://clearance.austinjamaat.org/letter" target="_blank" rel="noopener noreferrer">here</a></li>
                                <li>Take raza from Janaab Aamil Saheb and araz the letter when requesting Raza</li>
                                <li>Create the event on this site</li>
                            </ol>
                        </div>
                    </div>
                );
            // case 'clearance':
            //     return (
            //         <div className="clearance-content">
            //             {qbOpens.length > 0 ? (
            //                 <>
            //                     <h2 className="step-title clearance-title">
            //                         Open pledge reminders
            //                     </h2>
            //                     <div className="clearance-table">
            //                         <table>
            //                             <tbody>
            //                                 {qbOpens.map((invoice) => (
            //                                     <tr key={invoice._id}>
            //                                         <td className="clearance-invoice-cell">{invoice.qb_id}</td>
            //                                         <td className="clearance-balance-cell">
            //                                             ${invoice.balance.toFixed(2)}
            //                                         </td>
            //                                     </tr>
            //                                 ))}
            //                             </tbody>
            //                         </table>
            //                     </div>
            //                     <a
            //                         href="https://www.billandpay.com/go/aeb"
            //                         target="_blank"
            //                         rel="noopener noreferrer"
            //                         className="clearance-pay-link"
            //                     >
            //                         Adaa Pledge
            //                     </a>
            //                 </>
            //             ) : (
            //                 <div className="clearance-clear">
            //                     <p>All pledges are clear. Shukran for your support.</p>
            //                 </div>
            //             )}
            //         </div>
            //     );
            case 'radio':
                return (
                    <div className="radio-options">
                        {currentStepData.options.map((option, index) => (
                            <div
                                key={index}
                                className={`radio-option ${formData[currentStepData.field] === option.value ? 'selected' : ''}`}
                                onClick={() => handleInputChange(currentStepData.field, option.value)}
                            >
                                <strong>{option.label}</strong>
                            </div>
                        ))}
                    </div>
                );
            case 'dateTime':
                const today = new Date().toISOString().split('T')[0];

                const hijriMonths = [
                    'Moharram al Haraam', 'Safar al Muzaffar', 'Rabi al Awwal', 'Rabi al Akhar',
                    'Jamad al Ula', 'Jamad al Akhar', 'Rajab al Asab', 'Shaban al Kareem',
                    'Shehrullah il Moazzam', 'Shawwal al Mukarram', 'Zilqadatil Haraam', 'Zilhijjatil Haraam'
                ];

                const parseTime = (timeStr) => {
                    if (!timeStr) return { hours: '7', minutes: '00', period: 'PM' };
                    // Handle case where hours might be empty (during editing)
                    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
                    if (match) {
                        return {
                            hours: match[1] || '7',
                            minutes: match[2],
                            period: match[3].toUpperCase()
                        };
                    }
                    // Handle case where time string starts with colon (empty hours)
                    const emptyHoursMatch = timeStr.match(/^:(\d{2})\s?(AM|PM)$/i);
                    if (emptyHoursMatch) {
                        return {
                            hours: '',
                            minutes: emptyHoursMatch[1],
                            period: emptyHoursMatch[2].toUpperCase()
                        };
                    }
                    return { hours: '7', minutes: '00', period: 'PM' };
                };

                const { hours, minutes, period } = parseTime(formData.time);

                const updateTime = (field, value) => {
                    const current = parseTime(formData.time);

                    if (field === 'hours') {
                        let numValue = parseInt(value);
                        if (isNaN(numValue) || numValue === 0) {
                            numValue = 12;
                        } else if (numValue > 12) {
                            numValue = 1;
                        } else if (numValue < 1) {
                            numValue = 12;
                        }
                        current.hours = numValue.toString();
                    } else if (field === 'minutes') {
                        let numValue = parseInt(value);
                        if (isNaN(numValue)) {
                            numValue = 0;
                        } else if (numValue > 59) {
                            numValue = 0;
                        } else if (numValue < 0) {
                            numValue = 59;
                        }
                        current.minutes = numValue.toString().padStart(2, '0');
                    } else if (field === 'period') {
                        current.period = value;
                    }
                    const timeString = `${current.hours}:${current.minutes} ${current.period}`;
                    handleInputChange('time', timeString);
                };

                return (
                    <div className="datetime-picker">
                        <div className="hijri-section">
                            <label className="datetime-label">Hijri Date</label>
                            <div className="hijri-date-picker">
                                <select
                                    value={formData.hijriDay || ''}
                                    onChange={(e) => handleInputChange('hijriDay', e.target.value)}
                                    className="step-select hijri-day-select"
                                    autoFocus
                                >
                                    <option value="">Day</option>
                                    {Array.from({ length: 30 }, (_, i) => i + 1).map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                                <select
                                    value={formData.hijriMonth || ''}
                                    onChange={(e) => handleInputChange('hijriMonth', e.target.value)}
                                    className="step-select hijri-month-select"
                                >
                                    <option value="">Month</option>
                                    {hijriMonths.map((month, index) => (
                                        <option key={index} value={month}>{month}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="date-section">
                            <label className="datetime-label">Gregorian Date</label>
                            <input
                                type="date"
                                value={formData.date || ''}
                                onChange={(e) => handleInputChange('date', e.target.value)}
                                min={today}
                                className="step-input date-input"
                            />
                        </div>
                        <div className="time-section">
                            <label className="datetime-label">Time</label>
                            <div className="time-picker">
                                <div className="time-display">
                                    <div className="time-input-group">
                                        <input
                                            type="number"
                                            value={hours}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    // Allow empty value temporarily while user is typing
                                                    const current = parseTime(formData.time);
                                                    current.hours = '';
                                                    const timeString = `:${current.minutes} ${current.period}`;
                                                    handleInputChange('time', timeString);
                                                } else if (value === '0') {
                                                    updateTime('hours', '12');
                                                } else {
                                                    let numValue = parseInt(value);
                                                    if (!isNaN(numValue) && numValue > 0 && numValue <= 12) {
                                                        updateTime('hours', value);
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || value === '0') {
                                                    updateTime('hours', '7');
                                                } else {
                                                    let numValue = parseInt(value);
                                                    if (isNaN(numValue)) {
                                                        updateTime('hours', '7');
                                                    } else if (numValue > 12) {
                                                        updateTime('hours', '1');
                                                    } else if (numValue < 1) {
                                                        updateTime('hours', '12');
                                                    }
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    const currentHour = parseInt(hours) || 7;
                                                    const newHour = currentHour >= 12 ? 1 : currentHour + 1;
                                                    updateTime('hours', newHour.toString());
                                                } else if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    const currentHour = parseInt(hours) || 7;
                                                    const newHour = currentHour <= 1 ? 12 : currentHour - 1;
                                                    updateTime('hours', newHour.toString());
                                                }
                                            }}
                                            placeholder="7"
                                            className="time-input hours-input"
                                        />
                                        <span className="time-separator">:</span>
                                        <input
                                            type="number"
                                            value={minutes}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    updateTime('minutes', '0');
                                                } else {
                                                    updateTime('minutes', value);
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    updateTime('minutes', '0');
                                                } else {
                                                    let numValue = parseInt(value);
                                                    if (isNaN(numValue)) {
                                                        updateTime('minutes', '0');
                                                    } else if (numValue > 59) {
                                                        updateTime('minutes', '0');
                                                    } else if (numValue < 0) {
                                                        updateTime('minutes', '59');
                                                    }
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    const currentMinute = parseInt(minutes) || 0;
                                                    const newMinute = currentMinute >= 59 ? 0 : currentMinute + 1;
                                                    updateTime('minutes', newMinute.toString());
                                                } else if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    const currentMinute = parseInt(minutes) || 0;
                                                    const newMinute = currentMinute <= 0 ? 59 : currentMinute - 1;
                                                    updateTime('minutes', newMinute.toString());
                                                }
                                            }}
                                            placeholder="00"
                                            className="time-input minutes-input"
                                        />
                                    </div>
                                    <div className="period-selector">
                                        <button
                                            type="button"
                                            className={`period-btn ${period === 'AM' ? 'active' : ''}`}
                                            onClick={() => updateTime('period', 'AM')}
                                        >
                                            AM
                                        </button>
                                        <button
                                            type="button"
                                            className={`period-btn ${period === 'PM' ? 'active' : ''}`}
                                            onClick={() => updateTime('period', 'PM')}
                                        >
                                            PM
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'address':
                return (
                    <input
                        type="text"
                        value={formData[currentStepData.field] || ''}
                        onChange={(e) => {
                            handleInputChange(currentStepData.field, e.target.value);
                        }}
                        placeholder={currentStepData.placeholder}
                        className="step-input address-input"
                        autoFocus
                        autoComplete="address-line1"
                    />
                );
            case 'eventDetails':
                const handleEventDetailChange = (field, value) => {
                    const newEventDetails = {
                        ...formData.eventDetails,
                        [field]: value
                    };
                    handleInputChange('eventDetails', newEventDetails);
                };

                return (
                    <div className="event-details-container">
                        <div className="event-detail-field">
                            <label className="event-detail-label">Name your Event</label>
                            <input
                                type="text"
                                value={formData.eventDetails.eventName || ''}
                                onChange={(e) => handleEventDetailChange('eventName', e.target.value)}
                                placeholder="ex - Murtaza's Waras"
                                className="step-input event-detail-input"
                                autoFocus
                            />
                        </div>
                        <div className="event-detail-field">
                            <label className="event-detail-label">Program Details</label>
                            <input
                                type="text"
                                value={formData.eventDetails.program || ''}
                                onChange={(e) => handleEventDetailChange('program', e.target.value)}
                                placeholder="ex - Namaaz, Darees, Niyaaz"
                                className="step-input event-detail-input"
                            />
                        </div>
                    </div>
                );
            case 'text':
                return (
                    <input
                        type="text"
                        value={formData[currentStepData.field]}
                        onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                        placeholder={currentStepData.placeholder}
                        className="step-input"
                        autoFocus
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        value={formData[currentStepData.field]}
                        onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                        className="step-input"
                        autoFocus
                    />
                );
            case 'select':
                return (
                    <select
                        value={formData[currentStepData.field]}
                        onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                        className="step-select"
                        autoFocus
                    >
                        <option value="">Select an option...</option>
                        {currentStepData.options.map((option, index) => {
                            const value = typeof option === 'string' ? option : option.value;
                            const label = typeof option === 'string' ? option : option.label;
                            return (
                                <option key={index} value={value}>
                                    {label}
                                </option>
                            );
                        })}
                    </select>
                );
            case 'multiselect':
                if (currentStepData.field === 'hosts') {
                    const getFilteredHosts = () => {
                        if (!hostSearchTerm) return members;
                        return members.filter(member =>
                            member && member.fullName &&
                            member.fullName.toLowerCase().includes(hostSearchTerm.toLowerCase())
                        );
                    };

                    const selectHost = (member) => {
                        const newHosts = [...formData.hosts, { _id: member._id, name: member.fullName }];
                        handleInputChange('hosts', newHosts);
                        setHostSearchTerm('');
                        setShowHostDropdown(false);
                    };

                    const removeHost = (index) => {
                        const newHosts = formData.hosts.filter((_, i) => i !== index);
                        handleInputChange('hosts', newHosts);
                    };

                    return (
                        <div className="hosts-selection-container">
                            <div className="hosts-input-wrapper">
                                <input
                                    type="text"
                                    value={hostSearchTerm}
                                    onChange={(e) => setHostSearchTerm(e.target.value)}
                                    onFocus={() => setShowHostDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowHostDropdown(false), 200)}
                                    placeholder="Type to search..."
                                    className="step-input"
                                    autoComplete="off"
                                />
                                {showHostDropdown && hostSearchTerm && (
                                    <div className="hosts-dropdown">
                                        {getFilteredHosts().length > 0 ? (
                                            getFilteredHosts().slice(0, 10).map(member => (
                                                <div
                                                    key={member._id}
                                                    className="hosts-option"
                                                    onClick={() => selectHost(member)}
                                                >
                                                    {member.fullName}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="hosts-no-results">No members found</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {formData.hosts.length > 0 && (
                                <div className="selected-hosts">
                                    {formData.hosts.map((host, index) => (
                                        <div key={index} className="host-chip">
                                            <span>{host.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeHost(index)}
                                                className="remove-host-btn"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                } else if (currentStepData.field === 'hosts') {
                    return (
                        <div className="hosts-container">
                            <HostList
                                members={members}
                                onHostsChange={(hosts) => handleInputChange('hosts', hosts)}
                            />
                        </div>
                    );
                }

                const memberOptions = members
                    .filter(member => member && member.fullName)
                    .map(member => ({
                        value: member._id,
                        name: member.fullName,
                        _id: member._id
                    }));

                return (
                    <Multiselect
                        options={memberOptions}
                        selectedValues={formData[currentStepData.field]}
                        onSelect={(selectedList) => handleInputChange(currentStepData.field, selectedList)}
                        onRemove={(selectedList) => handleInputChange(currentStepData.field, selectedList)}
                        style={{
                            chips: { background: "#a4800f" },
                            option: { color: isHovering ? "#a4800f" : "#fff" }
                        }}
                        displayValue="name"
                        placeholder="Select hosts..."
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                    />
                );
            case 'checkbox':
                return (
                    <div className="checkbox-container">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData[currentStepData.field]}
                                onChange={(e) => handleInputChange(currentStepData.field, e.target.checked)}
                                className="step-checkbox"
                            />
                            <span className="checkbox-text">Yes, this is a women-only event</span>
                        </label>
                    </div>
                );
            case 'confirmation':
                const hijriDateString = formData.hijriDay && formData.hijriMonth
                    ? `${formData.hijriDay} ${formData.hijriMonth}`
                    : '';
                return (
                    <div className="confirmation-container">
                        <div className="confirmation-section">
                            <div className="confirmation-details">
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Name:</span>
                                    <span className="confirmation-value">{formData.eventDetails.eventName || 'Not specified'}</span>
                                </div>
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Hijri Date:</span>
                                    <span className="confirmation-value">{hijriDateString || 'Not specified'}</span>
                                </div>
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Gregorian Date:</span>
                                    <span className="confirmation-value">{formData.date || 'Not specified'}</span>
                                </div>
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Time:</span>
                                    <span className="confirmation-value">{formData.time || 'Not specified'}</span>
                                </div>
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Program:</span>
                                    <span className="confirmation-value">{formData.eventDetails.program || 'Not specified'}</span>
                                </div>
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Venue:</span>
                                    <span className="confirmation-value">{formData.isAtMarkaz ? 'Markaz' : formData.address || 'Not specified'}</span>
                                </div>
                                <div className="confirmation-item">
                                    <span className="confirmation-label">Hosts:</span>
                                    <span className="confirmation-value">
                                        {formData.hosts && formData.hosts.length > 0 ? formData.hosts.map(hostId => {
                                            const host = members.find(m => m._id === hostId);
                                            return host ? host.fullName : '';
                                        }).filter(Boolean).join(', ') : 'Not specified'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="multi-step-form">
            <div className="step-card">
                <div className="step-header">
                    <h2 className="step-title">{currentStepData.title}</h2>
                </div>

                <div className="step-content">
                    {renderInput()}
                </div>

                <div className="step-actions">
                    {currentStep > 0 && (
                        <button
                            className="step-button back-button"
                            onClick={prevStep}
                        >
                            ← Back
                        </button>
                    )}

                    {!isLastStep ? (
                        <button
                            className="step-button next-button"
                            onClick={nextStep}
                            disabled={!canProceed}
                        >
                            Next →
                        </button>
                    ) : (
                        <button
                            className="step-button submit-button"
                            onClick={handleSubmit}
                            disabled={!canProceed}
                        >
                            {currentStepData.type === 'confirmation' ? 'Confirm' : 'Create Event'}
                        </button>
                    )}
                </div>
                <div className="step-progress">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                        ></div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default CreateEventMultiStep;
